import { isDefined } from '@vueuse/core';
import { cloneDeep, isEqual } from 'lodash';

import {
  isHotelIngredientAdapter,
  isOwnStayIngredientAdapter,
} from '@apps/main/modules/recipe/ingredients';

import { SemboDate } from '@libs/sembo-dates';
import {
  AvailabilityOrder,
  IngredientType,
  SelectedHotelTypeQL,
} from '@libs/trip-planner-interfaces';
import type {
  BusinessCriteria,
  DestinationAvailabilityCriteria,
  HotelIngredientAdapter,
  ItinerarySearchUserCriteria,
  ItinerarySearchUserCriteriaDependencies,
  OwnStayIngredientAdapter,
} from '@libs/trip-planner-interfaces';

export interface ActivitySearchDates {
  startDate?: Date;
  endDate?: Date;
  selectedDate?: Date;
}

export function getActivityAvailabilitySearchDates(
  datesToSearch: ActivitySearchDates,
  numberOfNights: number
): ActivitySearchDates {
  if (!datesToSearch.selectedDate) {
    return datesToSearch;
  }

  return {
    startDate: datesToSearch.selectedDate,
    endDate: new SemboDate(datesToSearch.selectedDate)
      .add({ days: numberOfNights })
      .get(),
    selectedDate: datesToSearch.selectedDate,
  };
}

export function hasChangedOnlySelectedDate(
  dependencies: ItinerarySearchUserCriteriaDependencies,
  oldDependencies: ItinerarySearchUserCriteriaDependencies
): boolean {
  const {
    selectedDate,
    startSearchDate,
    endSearchDate,
    ...dependenciesWihoutSelectedDate
  } = dependencies;
  const {
    selectedDate: oldSelectedDate,
    startSearchDate: oldStartSearchDate,
    endSearchDate: oldEndSearchDate,
    ...oldDependenciesWihoutSelectedDate
  } = oldDependencies;

  const hasSameSelectedDate =
    (!isDefined(selectedDate) && !isDefined(oldSelectedDate)) ||
    (isDefined(selectedDate) &&
      isDefined(oldSelectedDate) &&
      new SemboDate(selectedDate).isSame(oldSelectedDate));

  const hasSameStartSearchDate =
    (!isDefined(startSearchDate) && !isDefined(oldStartSearchDate)) ||
    (isDefined(startSearchDate) &&
      isDefined(oldStartSearchDate) &&
      new SemboDate(startSearchDate).isSame(oldStartSearchDate));

  const hasSameEndSearchDate =
    (!isDefined(endSearchDate) && !isDefined(oldEndSearchDate)) ||
    (isDefined(endSearchDate) &&
      isDefined(oldEndSearchDate) &&
      new SemboDate(endSearchDate).isSame(oldEndSearchDate));

  const hasChangedSelectedDate = !hasSameSelectedDate;

  return (
    isEqual(
      dependenciesWihoutSelectedDate,
      oldDependenciesWihoutSelectedDate
    ) &&
    hasChangedSelectedDate &&
    hasSameStartSearchDate &&
    hasSameEndSearchDate
  );
}

export function hasChangedToSingleDaySearch(
  dependencies: ItinerarySearchUserCriteriaDependencies,
  oldDependencies: ItinerarySearchUserCriteriaDependencies
): boolean {
  const {
    selectedDate,
    startSearchDate,
    endSearchDate,
    ...nonDateDependencies
  } = dependencies;
  const {
    selectedDate: _oldSelectedDate,
    startSearchDate: _oldStartSearchDate,
    endSearchDate: _oldEndSearchDate,
    ...oldNonDateDependencies
  } = oldDependencies;

  return (
    isDefined(selectedDate) &&
    isDefined(startSearchDate) &&
    isDefined(endSearchDate) &&
    new SemboDate(startSearchDate).isSameDay(selectedDate) &&
    new SemboDate(endSearchDate).isSameDay(selectedDate) &&
    isEqual(nonDateDependencies, oldNonDateDependencies)
  );
}

export function getActivityDetailsFromPriceAvailabilityCriteria(params: {
  businessCriteria: BusinessCriteria;
  datesToSearch: ActivitySearchDates;
  ingredientIndex: number;
  itineraryCriteria: ItinerarySearchUserCriteria;
  productCode: string;
}): DestinationAvailabilityCriteria {
  const recipeIngredients = params.itineraryCriteria.ingredients
    ? cloneDeep(params.itineraryCriteria.ingredients)
    : null;

  const ingredientFromCriteria = recipeIngredients?.[params.ingredientIndex];

  if (
    ingredientFromCriteria &&
    isOwnStayIngredientAdapter(ingredientFromCriteria)
  ) {
    recipeIngredients[params.ingredientIndex] =
      adaptOwnStayIngredientForActivityDetailsFromPrice(ingredientFromCriteria);
  }

  const normalizedIngredientFromCriteria =
    recipeIngredients?.[params.ingredientIndex];

  if (
    normalizedIngredientFromCriteria &&
    (isHotelIngredientAdapter(normalizedIngredientFromCriteria) ||
      isOwnStayIngredientAdapter(normalizedIngredientFromCriteria))
  ) {
    normalizedIngredientFromCriteria.constraint.selectedHotelTypes = [
      SelectedHotelTypeQL.Golf,
      SelectedHotelTypeQL.Ticket,
    ];
    normalizedIngredientFromCriteria.constraint.boardBasis = [];
  }

  const recipeWithDates: ItinerarySearchUserCriteria = {
    ...params.itineraryCriteria,
    ingredients: recipeIngredients,
    startSearchDate: params.datesToSearch.startDate,
    endSearchDate: params.datesToSearch.endDate,
    pax: [{ numberOfAdults: 1 }],
  };

  return {
    userCriteria: {
      recipe: recipeWithDates,
      ingredientIndex: params.ingredientIndex,
      constraint: {
        hotelCodes: [params.productCode],
      },
      day: params.datesToSearch.selectedDate,
    },
    businessCriteria: params.businessCriteria,
  };
}

function adaptOwnStayIngredientForActivityDetailsFromPrice(
  ingredient: OwnStayIngredientAdapter
): HotelIngredientAdapter {
  return {
    ingredientType: IngredientType.Hotel,
    ingredientId: ingredient.ingredientId,
    numberOfNights: ingredient.numberOfNights,
    constraint: ingredient.constraint,
    subIngredients: ingredient.subIngredients,
    heartedHotels: [],
    suggestedBy: AvailabilityOrder.Popular,
  };
}
